/* eslint-disable no-bitwise */
export class FFT {

  private roots: Float32Array;
  private rev: Float32Array;
  private a = new Float32Array(2);
  private b = new Float32Array(2);
  private c = new Float32Array(2);
  private d = new Float32Array(2);
  private e1 = new Float32Array(2);
  private e2 = new Float32Array(2);

  private rootsRowSize: number;

  constructor(private length: number) {
    switch (length) {
      case 64: this.roots = getFFTTableShort64(); this.rootsRowSize = 2; break;
      case 512: this.roots = getFFTTableLong512(); this.rootsRowSize = 3; break;
      case 60: this.roots = getFFTTableShort60(); this.rootsRowSize = 2; break;
      case 480: this.roots = getFFTTableLong480(); this.rootsRowSize = 3; break;
      default: throw new Error(`Unexpected FFT length: ${length}`);
    }

    this.rev = new Float32Array(2 * length);
  }

  process(input: Float32Array, forward: boolean) {
    const length = this.length;
    const imOffset = (forward ? 2 : 1);
    const scale = (forward ? length : 1);
    const rev = this.rev;
    const roots = this.roots;
    const a = this.a;
    const b = this.b;
    const c = this.c;
    const d = this.d;
    const e1 = this.e1;
    const e2 = this.e2;


    // bit-reversal
    let ii = 0;
    for (let i = 0; i < length; i++) {
      rev[2 * i] = input[2 * ii];
      rev[2 * i + 1] = input[2 * ii + 1];

      let k = length >>> 1;
      while (ii >= k && k > 0) {
        ii -= k;
        k >>= 1;
      }

      ii += k;
    }

    for (let i = 0; i < length; i++) {
      input[2 * i] = rev[2 * i];
      input[2 * i + 1] = rev[2 * i + 1];
    }

    // bottom base-4 round
    for (let i = 0; i < length; i += 4) {
      a[0] = input[2 * i] + input[2 * (i + 1)];
      a[1] = input[2 * i + 1] + input[2 * (i + 1) + 1];
      b[0] = input[2 * (i + 2)] + input[2 * (i + 3)];
      b[1] = input[2 * (i + 2) + 1] + input[2 * (i + 3) + 1];
      c[0] = input[2 * i] - input[2 * (i + 1)];
      c[1] = input[2 * i + 1] - input[2 * (i + 1) + 1];
      d[0] = input[2 * (i + 2)] - input[2 * (i + 3)];
      d[1] = input[2 * (i + 2) + 1] - input[2 * (i + 3) + 1];
      input[2 * i] = a[0] + b[0];
      input[2 * i + 1] = a[1] + b[1];
      input[2 * (i + 2)] = a[0] - b[0];
      input[2 * (i + 2) + 1] = a[1] - b[1];

      e1[0] = c[0] - d[1];
      e1[1] = c[1] + d[0];
      e2[0] = c[0] + d[1];
      e2[1] = c[1] - d[0];

      if (forward) {
        input[2 * (i + 1)] = e2[0];
        input[2 * (i + 1) + 1] = e2[1];
        input[2 * (i + 3)] = e1[0];
        input[2 * (i + 3) + 1] = e1[1];
      } else {
        input[2 * (i + 1)] = e1[0];
        input[2 * (i + 1) + 1] = e1[1];
        input[2 * (i + 3)] = e2[0];
        input[2 * (i + 3) + 1] = e2[1];
      }
    }

    // iterations from bottom to top
    for (let i = 4; i < length; i <<= 1) {
      const shift = i << 1;
      const m = length / shift;

      for (let j = 0; j < length; j += shift) {
        for (let k = 0; k < i; k++) {
          const km = k * m;
          const rootRe = roots[this.rootsRowSize * km];
          const rootIm = roots[this.rootsRowSize * km + imOffset];
          const zRe = input[2 * (i + j + k)] * rootRe - input[2 * (i + j + k) + 1] * rootIm;
          const zIm = input[2 * (i + j + k)] * rootIm + input[2 * (i + j + k) + 1] * rootRe;

          input[2 * (i + j + k)] = (input[2 * (j + k)] - zRe) * scale;
          input[2 * (i + j + k) + 1] = (input[2 * (j + k) + 1] - zIm) * scale;
          input[2 * (j + k)] = (input[2 * (j + k)] + zRe) * scale;
          input[2 * (j + k) + 1] = (input[2 * (j + k) + 1] + zIm) * scale;
        }
      }
    }
  }

}



function generateFFTTableShort(length: number): Float32Array {
  const t = 2 * Math.PI / length;
  const cosT = Math.cos(t);
  const sinT = Math.sin(t);
  const f = new Float32Array(2 * length);

  f[0] = 1;
  f[1] = 0;
  let lastImag = 0;

  for (let i = 1; i < length; i++) {
    f[2 * i] = f[2 * (i - 1)] * cosT + lastImag * sinT;
    lastImag = lastImag * cosT - f[2 * (i - 1)] * sinT;
    f[2 * i + 1] = -lastImag;
  }

  return f;
}


function generateFFTTableLong(length: number): Float32Array {
  const t = 2 * Math.PI / length;
  const cosT = Math.cos(t);
  const sinT = Math.sin(t);
  const f = new Float32Array(3 * length);

  f[0] = 1;
  f[1] = 0;
  f[2] = 0;

  for (let i = 1; i < length; i++) {
    f[3 * i] = f[3 * (i - 1)] * cosT + f[3 * (i - 1) + 2] * sinT;
    f[3 * i + 2] = f[3 * (i - 1) + 2] * cosT - f[3 * (i - 1)] * sinT;
    f[3 * i + 1] = -f[3 * i + 2];
  }

  return f;
}


let FFTTableShort64: Float32Array | undefined;
function getFFTTableShort64(): Float32Array {
  if (FFTTableShort64 === undefined) {
    FFTTableShort64 = generateFFTTableShort(64);
  }
  return FFTTableShort64;
}


let FFTTableShort60: Float32Array | undefined;
function getFFTTableShort60(): Float32Array {
  if (FFTTableShort60 === undefined) {
    FFTTableShort60 = generateFFTTableShort(60);
  }
  return FFTTableShort60;
}


let FFTTableLong512: Float32Array | undefined;
function getFFTTableLong512(): Float32Array {
  if (FFTTableLong512 === undefined) {
    FFTTableLong512 = generateFFTTableLong(512);
  }
  return FFTTableLong512;
}


let FFTTableLong480: Float32Array | undefined;
function getFFTTableLong480(): Float32Array {
  if (FFTTableLong480 === undefined) {
    FFTTableLong480 = generateFFTTableLong(480);
  }
  return FFTTableLong480;
}
